import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import Storage from 'src/utils/storage/storage';
import { ProductSelection } from '../../../constants/constant';
import {
  addNewCommentForProductDrawerParentItem,
  addNewCommentForProductDrawerChildItem,
  updateCommentForProductDrawerParentItem,
  updateCommentForProductDrawerChildItem,
  deleteCommentFromProductDrawerParentItem,
  deleteCommentFromProductDrawerChildItem
} from '../../store/planner-slice';
import { getDrawerData } from '../../store/planner-slice-async-thunks';
import {
  IProductComment,
  IProductDetails
} from '../../types/ppg-sku-drawer.model';
import useProductComment from '../../hooks/product-comment-hook';

export default function useProductDrawer({
  toggle
}: {
  toggle: ProductSelection;
}) {
  const { id: scenarioId } = useParams();
  const { source: sourceId } = Storage.getSourceId();
  const { moduleNameToModuleIdMap } = useAppSelector(
    (state) => state.simulatorHome
  );
  const { productId, isLoading, product, ppgId } = useAppSelector(
    (state) => state.planner.drawerViewMetadata
  );
  const { planner: plannerAccessConfig = {} } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const { handleAddComment, handleDeleteComment, handleUpdateComment } =
    useProductComment();
  const appDispatch = useAppDispatch();

  const [childDetailsExpandableMap, setChildDetailsExpandableMap] = useState(
    {}
  );

  const collapseAll = () => {
    setChildDetailsExpandableMap(
      Object.keys(childDetailsExpandableMap)
        .map((key) => ({
          [`${key}`]: false
        }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {})
    );
  };

  const toggleExpansion = (indexOfChild: number) => {
    const isOpen = childDetailsExpandableMap[indexOfChild];
    setChildDetailsExpandableMap({
      ...childDetailsExpandableMap,
      [`${indexOfChild}`]: !isOpen
    });
  };

  useEffect(() => {
    appDispatch(
      getDrawerData({
        id: productId,
        moduleId: moduleNameToModuleIdMap.planner,
        scenarioId: Number(scenarioId),
        sourceId,
        toggle
      })
    ).then((response) => {
      const p = response.payload as IProductDetails;
      if (p.child_details.length) {
        setChildDetailsExpandableMap(
          Array.from(Array(p.child_details.length).keys()).reduce(
            (prev, curr) => ({ ...prev, [curr]: true }),
            {}
          )
        );
      }
    });
  }, []);

  return {
    handleAddComment: (
      comment: string,
      parentId: number,
      childId: number,
      itemIndex: number = null
    ) =>
      handleAddComment(comment, parentId, childId).then((response) => {
        if (itemIndex === null) {
          appDispatch(
            addNewCommentForProductDrawerParentItem(
              response.payload as IProductComment
            )
          );
        } else {
          appDispatch(
            addNewCommentForProductDrawerChildItem({
              commentIndex: itemIndex,
              comment: response.payload as IProductComment
            })
          );
        }
      }),
    handleUpdateComment: (
      commentId: number,
      updatedComment: string,
      itemIndex: number = null
    ) =>
      handleUpdateComment(commentId, updatedComment).then((response) => {
        if (itemIndex === null) {
          appDispatch(
            updateCommentForProductDrawerParentItem(
              response.payload as IProductComment
            )
          );
        } else {
          appDispatch(
            updateCommentForProductDrawerChildItem({
              itemIndex,
              comment: response.payload as IProductComment
            })
          );
        }
      }),
    handleDeleteComment: (commentId: number, itemIndex: number = null) =>
      handleDeleteComment(commentId).then(() => {
        if (itemIndex === null) {
          appDispatch(deleteCommentFromProductDrawerParentItem(commentId));
        } else {
          appDispatch(
            deleteCommentFromProductDrawerChildItem({
              itemIndex,
              commentId
            })
          );
        }
      }),
    isLoading,
    product,
    childDetailsExpandableMap,
    productId,
    ppgId,
    appDispatch,
    toggleExpansion,
    collapseAll,
    plannerAccessConfig
  };
}
